/**
 * devices/deviceCopy.ts — the words and the geometry for the session surfaces.
 *
 * ── WHY THIS FILE EXISTS AT ALL ───────────────────────────────────────────
 * `deviceLabel()` (lib/deviceId.ts) can only ever emit "{Browser} on {OS}",
 * deliberately — a stronger id would be a fingerprint we would then have to
 * defend. So a trader with a work Mac, a home Mac and a MacBook Air sees THREE
 * ROWS THAT SAY "Chrome on Mac", and the only thing that tells them apart is a
 * 12px timestamp. That is not a styling problem, it is an information problem,
 * and the server already hands us the answer: `firstSeenAt` ships in every
 * device projection (functions/src/devices/decide.ts::deviceView) and is
 * rendered nowhere in the product.
 *
 * So: two time facts per row ("Active 12 days ago", "in use since 2 Jul"), and
 * the *spacing between rows* made proportional to dormancy, so you don't
 * compare three timestamps — you SEE that one session has been asleep for
 * twelve days while the other two were used this morning.
 *
 * Everything here is pure and dependency-free so it can be unit-tested, which
 * a `calc()` chain in CSS cannot be.
 */
import type { DeviceSummary } from '../../lib/deviceSession'

/**
 * The axis runs from ONE HOUR to THIRTY DAYS, in log space.
 *
 * The floor is the interesting bit and it was measured, not guessed. Anchoring
 * the log at one MINUTE — the obvious first choice — makes "4 hours ago" score
 * 0.51, i.e. exactly half as dormant as a month, so a session used this morning
 * sits as far from a live one as a twelve-day-old laptop does from it. Rendered,
 * the three gaps came out 39px and 36px: a rail that is technically a time axis
 * and visually a constant. The whole claim of this surface is that "used today"
 * clusters and "dormant" hangs away, so the floor has to be the boundary of
 * "just now". At an hour: 4h → 0.21, 12d → 0.86, and the gaps read 25px vs 51px.
 */
const FLOOR_MINUTES = 60
const SPAN = Math.log10(43200 / FLOOR_MINUTES) // 30 days ÷ 1 hour

/** px between two sessions used at the same moment. */
export const MIN_GAP = 12
/**
 * px between "used a minute ago" and "asleep for a month". Capped, so twelve
 * devices could never push the primary CTA off a 568px-tall phone.
 */
export const MAX_GAP = 72

/**
 * Log-compressed age. 0 = used within the last hour, 1 = dormant a month or more.
 *
 * Log rather than linear because the interesting distinction is "today vs last
 * week", not "day 26 vs day 27" — a linear axis spends all its range on the
 * gap nobody is deciding between.
 *
 * An unparseable date resolves to 0.5, never NaN: a NaN would flow into a
 * `marginTop` and collapse the whole rail.
 */
export function ageWeight(lastSeenAt: string | null | undefined, now = Date.now()): number {
  const t = Date.parse(String(lastSeenAt ?? ''))
  if (!Number.isFinite(t)) return 0.5
  const hours = (now - t) / 60000 / FLOOR_MINUTES
  if (!Number.isFinite(hours)) return 0.5
  const w = Math.log10(Math.max(1, hours)) / SPAN
  if (!Number.isFinite(w)) return 0.5
  return Math.min(1, Math.max(0, w))
}

/**
 * The gap ABOVE each row, in px. Index 0 is always 0 — the first row sits at
 * the top of its container and the rail starts at its node.
 *
 * The list arrives newest-first (the server sorts it that way), so the weights
 * are non-decreasing and the deltas are non-negative; the clamp is belt and
 * braces for a list handed to us out of order.
 */
export function railGaps(devices: Array<{ lastSeenAt?: string | null }>, now = Date.now()): number[] {
  const weights = devices.map((d) => ageWeight(d.lastSeenAt, now))
  return weights.map((w, i) => {
    if (i === 0) return 0
    const delta = w - weights[i - 1]
    const gap = MIN_GAP + delta * (MAX_GAP - MIN_GAP)
    return Math.round(Math.min(MAX_GAP, Math.max(MIN_GAP, gap)))
  })
}

/**
 * "Active now" / "12 days ago" — the coarse recency line. Kept identical to the
 * wording that shipped in DeviceGate so nothing regresses for a user who has
 * seen this screen before.
 */
export function lastSeen(iso: string | null | undefined, now = Date.now()): string {
  const t = Date.parse(String(iso ?? ''))
  if (!Number.isFinite(t)) return 'Recently'
  const mins = Math.max(0, Math.round((now - t) / 60000))
  if (mins < 3) return 'Active now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`
  return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * "in use since 6 Aug" — the DISAMBIGUATOR. Two identical "Chrome on Mac" rows
 * that were both active this morning are told apart by which one you have been
 * signed in on since March.
 *
 * Returns null for a session that started today: "in use since today" adds a
 * line and no information.
 */
export function firstSeen(iso: string | null | undefined, now = Date.now()): string | null {
  const t = Date.parse(String(iso ?? ''))
  if (!Number.isFinite(t)) return null
  if (now - t < 20 * 60 * 60 * 1000) return null
  return `in use since ${new Date(t).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`
}

/**
 * The accessible name for one session row — a full SENTENCE, not the visual
 * fragments. A blind user picking a session to end must not simply hear
 * "Chrome on Mac" three times.
 */
export function sessionSentence(
  d: Pick<DeviceSummary, 'label' | 'lastSeenAt' | 'firstSeenAt' | 'current'>,
  opts: { suggested?: boolean; now?: number } = {},
): string {
  const now = opts.now ?? Date.now()
  const parts = [d.label.replace(/\.$/, '')]
  if (d.current) parts.push('This device')
  // `lastSeen` already returns a whole phrase for the two edge cases, so
  // prefixing blindly produces "Active active now" — which is what a screen
  // reader would then say, out loud, three times.
  const seen = lastSeen(d.lastSeenAt, now)
  parts.push(seen === 'Active now' ? 'Active now' : seen === 'Recently' ? 'Recently active' : `Last active ${seen}`)
  const since = firstSeen(d.firstSeenAt, now)
  if (since) parts.push(since.charAt(0).toUpperCase() + since.slice(1))
  if (opts.suggested) parts.push('Suggested — least recently used')
  return `${parts.join('. ')}.`
}

/**
 * Which session to PRE-SELECT: the least recently active one.
 *
 * This is the single biggest usability delta on the interstitial. Before this,
 * `selected` started null and the primary read "Choose a device to sign out",
 * disabled — the screen demanded a decision before it would let you act. The
 * plan has always said the least-recently-active device is *suggested*;
 * nothing implemented it. With it, the common case is one keystroke.
 */
export function suggestedEvictionId(
  devices: Array<Pick<DeviceSummary, 'deviceId' | 'lastSeenAt' | 'current'>>,
): string | null {
  const others = devices.filter((d) => !d.current)
  if (others.length === 0) return null
  let best = others[0]
  let bestT = Date.parse(String(best.lastSeenAt ?? ''))
  if (!Number.isFinite(bestT)) bestT = Infinity
  for (const d of others.slice(1)) {
    const t = Date.parse(String(d.lastSeenAt ?? ''))
    // An unparseable date is never "the obvious one to kill".
    const cmp = Number.isFinite(t) ? t : Infinity
    if (cmp < bestT) { best = d; bestT = cmp }
  }
  return best.deviceId
}

/* ───────────────────── the shared-login detector ────────────────────────
 *
 * In a B2B exchange the most common real cause of a device cap is NOT three of
 * your own machines — it is two humans on one company login, who will now
 * evict each other forever. Counting evictions locally is enough to notice
 * that, costs no backend, stores no PII, and points the most annoying moment
 * in the product at the thing that actually fixes it: a second seat.
 */

const EVICTION_KEY = 'diaex.evictions'
const DAY_MS = 24 * 60 * 60 * 1000

function readEvictions(now: number): number[] {
  try {
    const raw = window.localStorage.getItem(EVICTION_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((n): n is number => typeof n === 'number' && Number.isFinite(n))
      .filter((n) => now - n < DAY_MS && n <= now)
  } catch {
    return []
  }
}

/** How many times this browser has been evicted in the last 24 hours. */
export function evictionCount(now = Date.now()): number {
  return readEvictions(now).length
}

/** Record one eviction and return the new 24h count. Storage-failure safe. */
export function recordEviction(now = Date.now()): number {
  const next = [...readEvictions(now), now].slice(-8)
  try {
    window.localStorage.setItem(EVICTION_KEY, JSON.stringify(next))
  } catch {
    /* private mode — the note just won't fire, which is the safe direction */
  }
  return next.length
}

/** Test hook / a clean slate after the user acts on the note. */
export function clearEvictions(): void {
  try { window.localStorage.removeItem(EVICTION_KEY) } catch { /* nothing to do */ }
}
